import { useEffect, useState } from 'react';

import { timeoutFetchSentryGuard } from 'Fetch';
import { getSellerQuote } from 'PrivateApis';

import { useFeatureFlags } from 'Utilities/hooks';

import {
  QuotePriceRange, QuoteResponse, SellerQuote,
} from '../@types';

const normaliseQuote = (response: QuoteResponse): SellerQuote => ({
  formattedPrice: response.formattedPrice,
  price: response.price.amount,
  priceRange: response.priceRange
    ? {
      formattedHigh: response.priceRange.high.formattedAmount,
      formattedLow: response.priceRange.low.formattedAmount,
      formattedRange: response.formattedPrice,
      high: response.priceRange.high.amount,
      low: response.priceRange.low.amount,
    }
    : null,
});

type UseSellerQuoteProps = {
  dpEnquiryId?: number | null;
  enabled?: boolean;
};

type UseSellerQuoteReturn = {
  isLoading: boolean;
  priceRange?: QuotePriceRange | null;
  quote?: SellerQuote;
  rangeDisplayPrice: string | null;
};

type LoadedQuote = {
  dpEnquiryId: number;
  quote?: SellerQuote;
};

export const useSellerQuote = ({ dpEnquiryId, enabled = true }: UseSellerQuoteProps): UseSellerQuoteReturn => {
  const { useQuoteService } = useFeatureFlags();
  const isEnabled = Boolean(useQuoteService) && enabled;

  const [loaded, setLoaded] = useState<LoadedQuote>();

  useEffect(() => {
    if (!isEnabled || !dpEnquiryId) {
      setLoaded(undefined);
      return undefined;
    }

    const abortController = new AbortController();
    let active = true;

    setLoaded(undefined);

    getSellerQuote(dpEnquiryId, abortController)
      .then((result) => {
        if (active) {
          setLoaded({ dpEnquiryId, quote: result ? normaliseQuote(result) : undefined });
        }
      })
      .catch((err) => {
        if (!active) {
          return;
        }

        timeoutFetchSentryGuard(err, () => window?.Sentry?.captureException(err, {
          extra: { dpEnquiryId },
          tags: { fetch: 'Fetch seller quote' },
        }));

        setLoaded({ dpEnquiryId, quote: undefined });
      });

    return () => {
      active = false;
      abortController.abort();
    };
  }, [dpEnquiryId, isEnabled]);

  const isCurrent = Boolean(loaded && dpEnquiryId && loaded.dpEnquiryId === dpEnquiryId);
  const quote = isCurrent ? loaded?.quote : undefined;

  return {
    isLoading: Boolean(isEnabled && dpEnquiryId) && !isCurrent,
    priceRange: quote?.priceRange,
    quote,
    rangeDisplayPrice: quote?.formattedPrice ?? null,
  };
};
